import React, { useState, useEffect, useContext, useRef } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { FiMail, FiLock } from "react-icons/fi";
import { FaArrowLeft } from "react-icons/fa"
import ProfilePictureSettings from "../utils/profilePictureSettings";
import blank_pp from "../assets/blank_pp.webp";
import backgroundImg from "../assets/login.webp";
import { setUser } from "../store/slices/authSlice";
import { useLoginUserMutation, useAcceptAgreementMutation } from "../store/apis/authApi";

import Modal from "../components/Modal";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfUse from "./TermsOfUse";
import DemoAgreement from "./DemoAgreement";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useContext(ProfilePictureSettings);

  const [loginUser] = useLoginUserMutation();
  const [acceptAgreement] = useAcceptAgreementMutation();

   // UI State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Legal modals
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Demo flow
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const demoAcceptedRef = useRef(false);


  const tokenDataRef = useRef({ tokens: null, apiKey: null});
  

  
  // Continuous protection: restore session storage if it gets cleared
  useEffect(() => {
    if (window.__REACT_SNAP__) return;
    const interval = setInterval(() => {
      if (tokenDataRef.current.tokens && tokenDataRef.current.apiKey) {
        const tokensInStorage = sessionStorage.getItem("tokens");
        const apiKeyInStorage = sessionStorage.getItem("api_key");
        
        if (!tokensInStorage || !apiKeyInStorage) {
          sessionStorage.setItem("tokens", JSON.stringify(tokenDataRef.current.tokens));
          sessionStorage.setItem("api_key", JSON.stringify(tokenDataRef.current.apiKey));
        }
      }
    }, 50); // Check every 50ms

    return () => clearInterval(interval);
  }, []);


  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("Please enter both email and password");
      setLoading(false);
      return;
    }

    try {
      const response = await loginUser({ email, password }).unwrap();

      if (response.status === "success") {
        const tokens = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
        };
        const apiKey = { user_api_key: response.user_api_key };

        // Save to ref FIRST (primary source of truth)
        tokenDataRef.current = { tokens, apiKey };

        // Save to sessionStorage SECOND
        sessionStorage.setItem("tokens", JSON.stringify(tokens));
        sessionStorage.setItem("api_key", JSON.stringify(apiKey));

        // Update profile picture
        settings.updateProfilePictureSettings(
          response.profile_picture || blank_pp
        );

        // Check if demo user
        if (response.demo_user) {
          demoAcceptedRef.current = false; // Reset acceptance flag
          setIsDemoModalOpen(true);
        } else {
          navigate("/landing");
        }
      } else {
        setError("Login failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDemoAgreement = async() => {
    // Set flag that demo was accepted
    demoAcceptedRef.current = true;
    
    // Ensure data is in sessionStorage RIGHT NOW
    const { tokens, apiKey } = tokenDataRef.current;
    
    if (tokens && apiKey) {
      sessionStorage.setItem("tokens", JSON.stringify(tokens));
      sessionStorage.setItem("api_key", JSON.stringify(apiKey));
    } else {
      console.error("❌ ERROR: No tokens in ref!");
      setError("Session data missing. Please try logging in again.");
      return;
    };

    // Call API to update accept_agreement to true
    try {
      const payload = {
        field: "accept_agreement",
        value: true,
      };
    
      const data = await acceptAgreement(payload).unwrap();
    } catch (error) {
      console.error("❌ Error accepting agreement:", error);
      setError("Failed to save agreement. Please try again.");
      demoAcceptedRef.current = false;
      return;
    }
  };

  const handleDemoModalClose = () => {  
    // Check if demo was accepted
    if (demoAcceptedRef.current) {

      // Final verification before navigation
      const { tokens, apiKey } = tokenDataRef.current;
      if (tokens && apiKey) {
        sessionStorage.setItem("tokens", JSON.stringify(tokens));
        sessionStorage.setItem("api_key", JSON.stringify(apiKey));
      }
      
      // Close modal
      setIsDemoModalOpen(false);
      
      // Navigate after a brief delay
      setTimeout(() => {
        navigate("/landing", { replace: true });
      }, 100);
    } else {
      sessionStorage.removeItem("tokens");
      sessionStorage.removeItem("api_key");

      tokenDataRef.current = { tokens: null, apiKey: null };
        
      setIsDemoModalOpen(false);
        
      // Reset form
      setEmail("");
      setPassword("");
      
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Login | SolarAIDD</title>
        <meta name="description" content="Access your account to upload PV EL images and view defect detection results." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://solaraidd.csir.co.za/login" />
      </Helmet>

      {/* 🔙 Back Button */}
      <BackButton onClick={() => navigate("/")}>
        <FaArrowLeft size={20} />
        <span>Back</span>
      </BackButton>

      <LoginCard
        as={motion.div}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <h2>Welcome to SolarAIDD</h2>
        <p>Please log in to your account</p>

        <Form onSubmit={handleLogin}>
          {error && <Error>{error}</Error>}

          <InputWrapper>
            <FiMail />
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputWrapper>

          <InputWrapper>
            <FiLock />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </InputWrapper>

          <CheckboxContainer>
            <input
              type="checkbox"
              checked={showPassword}
              onChange={() => setShowPassword(!showPassword)}
            />
            <label>Show Password</label>
          </CheckboxContainer>

          <Button type="submit" disabled={loading || isDemoModalOpen}>
            {loading ? <span className="spinner" /> : "Login"}
          </Button>

          {/* Terms + Privacy links */}
          <Legal>
            By logging in, you agree to our{" "}
            <a onClick={() => setShowTerms(true)}>Terms of Use</a> and{" "}
            <a onClick={() => setShowPrivacy(true)}>Privacy Policy</a>.
          </Legal>
        </Form>

        <Links>
          <Link to="/forgot-password">Forgot Password?</Link>
          <span>
            Don’t have an account? <Link to="/signupv2">Sign up</Link>
          </span>
        </Links>
      </LoginCard>

      {/* Modal for Terms of Use */}
      <Modal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        title="Terms of Use"
        lastUpdated="September 1, 2025"
        onAccept={() => console.log("✅ Terms accepted")}
      >
        <TermsOfUse/>
      </Modal>

      {/* Modal for Privacy Policy */}
      <Modal
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        title="Privacy Policy"
        lastUpdated="August 15, 2025"
        onAccept={() => console.log("✅ Privacy accepted")}
      >
        <PrivacyPolicy/>
      </Modal>
      
      {/* Demo Agreement Modal - onClose handles both accept and decline */}
      {isDemoModalOpen && (
        <Modal
          isOpen={true}
          onClose={handleDemoModalClose}
          title="User Acceptance Agreement"
          lastUpdated="October 5, 2025"
          onAccept={handleAcceptDemoAgreement}
        >
          <DemoAgreement />
        </Modal>
      )}
    </PageContainer>
  );
};

export default Login;

// 🔹 Styled Components
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  background-image: url(${backgroundImg});
  background-size: cover;
  background-position: center;
  justify-content: center;
  align-items: center;
`;

const BackButton = styled.button`
  position: absolute;
  top: 30px;
  left: 30px;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.65);
  text-align: center;
  max-width: 400px;
  width: 100%;
  color: white;

  h2 {
    margin-bottom: 0.5rem;
    font-size: 2rem;
  }
  p {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    color: #f0f0f0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 0.8rem 1rem;
  svg {
    margin-right: 0.8rem;
    color: #fff;
  }
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: white;
  outline: none;
  font-size: 1rem;

  &::placeholder {
    color: #ddd;
  }
`;

const Button = styled.button`
  padding: 0.9rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #ff6a00, #ee0979);
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid white;
    border-top: 2px solid transparent;
    border-radius: 50%;
    display: inline-block;
    animation: spin 0.6s linear infinite;
  }
  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Error = styled.p`
  color: #ff4d4f; /* strong red */
  font-size: 0.9rem;
  margin-bottom: 10px;
  font-weight: 600; /* 🔹 make it bolder */
  text-align: left; /* 🔹 aligns with inputs */
  background: rgba(255, 77, 79, 0.1); /* 🔹 faint background */
  padding: 0.5rem;
  border-radius: 6px;

  animation: shake 0.3s ease;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #ddd;
`;

const Legal = styled.div`
  margin-top: 1rem;
  font-size: 0.75rem;
  color: #ddd;
  text-align: center;
  line-height: 1.4;

  a {
    color: #ffd369;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      text-decoration: underline;
      color: #ffae00;
    }
  }
`;

const Links = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  font-size: 0.85rem;

  a {
    color: #ffd369;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;